import React, { useState } from 'react';
import { Row, Col, Badge, Button } from 'react-bootstrap';
import { PlayFill } from 'react-bootstrap-icons';
import { getDueDateStatus, getTaskBorderColor } from '../../shared/utils/taskColors';
import QuickLabelAdd from './QuickLabelAdd';
import './EisenhowerMatrix.css';
import './CustomLabel.css';

const EisenhowerMatrix = ({ tasks, onToggleTaskStatus, onEditTask, onAddTaskProgress, onStartPomodoro, onUpdateTaskStatus, activeProfileId, onTaskUpdate, taskProgress, setSelectedTask, setShowTaskProgressHistory }) => {
    const [draggedTask, setDraggedTask] = useState(null);
    const [dragOverQuadrant, setDragOverQuadrant] = useState(null);

    const handleDragStart = (e, task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, quadrantKey) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverQuadrant(quadrantKey);
    };

    const handleDragLeave = () => {
        setDragOverQuadrant(null);
    };

    const handleDrop = (e, quadrantKey) => {
        e.preventDefault();
        setDragOverQuadrant(null);
        
        if (draggedTask && draggedTask.eisenhower_quadrant !== quadrantKey) {
            if (onUpdateTaskStatus) {
                onUpdateTaskStatus(draggedTask.id, { eisenhower_quadrant: quadrantKey });
            }
        }
        setDraggedTask(null);
    };

    const handleDragEnd = () => {
        setDraggedTask(null);
        setDragOverQuadrant(null);
    };

    const quadrants = {
        DO: { title: 'Do (Urgent & Important)', color: 'danger', tasks: [] },
        DECIDE: { title: 'Decide (Important, Not Urgent)', color: 'warning', tasks: [] },
        DELEGATE: { title: 'Delegate (Urgent, Not Important)', color: 'info', tasks: [] },
        DELETE: { title: 'Delete (Neither)', color: 'secondary', tasks: [] }
    };

    tasks.forEach(task => {
        const quadrant = task.eisenhower_quadrant || 'DO';
        if (quadrants[quadrant]) {
            quadrants[quadrant].tasks.push(task);
        }
    });

    return (
        <div className="eisenhower-matrix">
            <h4 className="mb-4">Eisenhower Matrix</h4>
            <Row>
                {Object.entries(quadrants).map(([key, quadrant]) => (
                    <Col md={6} key={key} className="mb-4">
                        <div className="quadrant-card">
                            <div className={`card-header bg-${quadrant.color} text-white`}>
                                <h6>{quadrant.title}</h6>
                                <Badge bg="light" text="dark">{quadrant.tasks.length}</Badge>
                            </div>
                            <div 
                                className={`quadrant-body p-3 ${dragOverQuadrant === key ? 'drag-over' : ''}`}
                                onDragOver={(e) => handleDragOver(e, key)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, key)}
                            >
                                {quadrant.tasks.length === 0 ? (
                                    <p className="text-muted small text-center mt-5">Drop tasks here</p>
                                ) : (
                                    quadrant.tasks.map(task => (
                                        <div 
                                            key={task.id} 
                                            className={`task-card ${getTaskBorderColor(task)} border-start border-3 ${draggedTask?.id === task.id ? 'dragging' : ''}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, task)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <div className="d-flex align-items-start justify-content-between">
                                                <div className="d-flex align-items-start">
                                                    <input
                                                        type="checkbox"
                                                        checked={task.status === 'COMPLETED'}
                                                        onChange={() => onToggleTaskStatus(task.id, task.status)}
                                                        className="me-2 mt-1"
                                                    />
                                                    <div onClick={() => onEditTask && onEditTask(task)}>
                                                        <div className="task-title">{task.title}</div>
                                                        {task.description && <div className="task-description">{task.description}</div>}
                                                        <div className="mt-2">
                                                            {task.labels && task.labels.map(label => (
                                                                <span 
                                                                    key={label.id} 
                                                                    className="badge me-1"
                                                                    style={{ 
                                                                        backgroundColor: label.color, 
                                                                        color: 'white',
                                                                        fontSize: '0.75em',
                                                                        padding: '0.25em 0.5em',
                                                                        borderRadius: '0.375rem'
                                                                    }}
                                                                >
                                                                    {label.name}
                                                                </span>
                                                            ))}
                                                            <QuickLabelAdd 
                                                                task={task}
                                                                activeProfileId={activeProfileId}
                                                                onTaskUpdate={onTaskUpdate}
                                                            />
                                                        </div>
                                                        {task.due_date && (
                                                            <div className="mt-1">
                                                                <Badge bg={getDueDateStatus(task.due_date)?.color || 'secondary'} className="task-due-date">
                                                                    {getDueDateStatus(task.due_date)?.text}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                        {taskProgress && taskProgress[task.id] && taskProgress[task.id].length > 0 && (
                                                            <div className="mt-1">
                                                                <Badge 
                                                                    bg="info" 
                                                                    style={{cursor: 'pointer'}}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedTask(task);
                                                                        setShowTaskProgressHistory(true);
                                                                    }}
                                                                >
                                                                    📈 {taskProgress[task.id].length} Progress Entries
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-1">
                                                    {onAddTaskProgress && (
                                                        <Button 
                                                            variant="outline-info" 
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onAddTaskProgress(task);
                                                            }}
                                                            title="Log Progress"
                                                        >
                                                            📈
                                                        </Button>
                                                    )}
                                                    {onStartPomodoro && (
                                                        <Button 
                                                            variant="outline-success" 
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onStartPomodoro(task);
                                                            }}
                                                            title="Start Pomodoro Timer"
                                                        >
                                                            <PlayFill size={16} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default EisenhowerMatrix;
